(function () {
  const PREFERENCE_KEY = 'pe-cart-shipping-protection-enabled';
  const DEFAULT_VARIANT_ID = '53435807138129';
  const API_SECTION_ID = 'api-cart-items';
  const ERROR_MESSAGE = 'De verzendverzekering kon niet worden bijgewerkt. Probeer het opnieuw.';
  const GIFT_MATCH_TERMS = ['schilderen', 'diamond'];

  if (window.__cartDrawerShippingProtectionInit) return;
  window.__cartDrawerShippingProtectionInit = true;

  class CartDrawerShippingProtection {
    constructor() {
      this.awaitingFallbackRefresh = false;
      this.cartSignalTimer = 0;
      this.fallbackSignalResetTimer = 0;
      this.failedSyncAt = 0;
      this.isProcessing = false;
      this.ignoringFallbackSignals = false;
      this.isThemeCartBuildWrapped = false;
      this.pendingJob = null;
      this.memoryPreference = true;
      this.state = {
        variantId: DEFAULT_VARIANT_ID,
        loading: false,
        errorMessage: '',
        lastConfirmedPreference: this.getStoredPreference(),
      };

      this.handleToggleChange = this.handleToggleChange.bind(this);
      this.handleCartSignal = this.handleCartSignal.bind(this);
      this.handleDrawerRemoveClick = this.handleDrawerRemoveClick.bind(this);
      this.handleDrawerQuantityUpdate = this.handleDrawerQuantityUpdate.bind(this);
    }

    init() {
      if (!this.getDrawer()) return;

      this.wrapThemeCartBuild();
      this.syncThemeCartReferences();
      this.prehydrateStoredPreference();

      document.addEventListener('change', this.handleToggleChange);
      document.addEventListener('click', this.handleDrawerRemoveClick, true);
      document.addEventListener('theme:cart:update', this.handleDrawerQuantityUpdate, true);
      document.addEventListener('theme:cart:change', this.handleCartSignal);
      document.addEventListener('theme:cart:open', this.handleCartSignal);
      document.addEventListener('theme:product:added', this.handleCartSignal);

      this.scheduleReconcile({
        reason: 'init',
        refreshSummary: true,
        modePriority: 0,
        respectStoredPreference: true,
      });
    }

    getRoute(key, fallback) {
      if (window.routes && window.routes[key]) return window.routes[key];
      if (window.theme && window.theme.routes && window.theme.routes[key]) return window.theme.routes[key];
      return fallback;
    }

    getDrawer() {
      return document.querySelector('[data-cart-drawer]');
    }

    getSummaryHolder() {
      return this.getDrawer()?.querySelector('[data-cart-summary-holder]') || null;
    }

    getProtectionBlock() {
      return this.getDrawer()?.querySelector('[data-owa-shipping-protection]') || null;
    }

    getToggle() {
      return this.getDrawer()?.querySelector('[data-owa-shipping-protection-toggle]') || null;
    }

    getErrorNode() {
      return this.getDrawer()?.querySelector('[data-owa-shipping-protection-error]') || null;
    }

    getGiftPromo() {
      return this.getDrawer()?.querySelector('[data-cart-gift-promo]') || null;
    }

    isGiftEligibleItem(item) {
      const productTitle = String(item?.product_title || item?.title || '').toLowerCase();
      return GIFT_MATCH_TERMS.some((term) => productTitle.includes(term));
    }

    getDrawerItem(node) {
      const drawer = this.getDrawer();
      const item = node?.closest?.('[data-item]') || null;

      if (!drawer || !item || !drawer.contains(item)) {
        return null;
      }

      return item;
    }

    isDisabledControl(node) {
      return Boolean(
        node?.hasAttribute?.('disabled')
        || node?.getAttribute?.('aria-disabled') === 'true'
        || node?.classList?.contains('is-disabled')
      );
    }

    getCartChangeJsonUrl() {
      const route = this.getRoute('cart_change_url', '/cart/change');
      return route.endsWith('.js') ? route : `${route}.js`;
    }

    syncDrawerLineIndexes(cart) {
      const drawer = this.getDrawer();

      if (!drawer || !cart || !Array.isArray(cart.items)) {
        return;
      }

      const lineIndexByKey = new Map();

      cart.items.forEach((item, index) => {
        if (item?.key) {
          lineIndexByKey.set(String(item.key), String(index + 1));
        }
      });

      drawer.querySelectorAll('[data-item]').forEach((itemNode) => {
        const itemKey = itemNode.dataset.item || '';
        const lineIndex = lineIndexByKey.get(itemKey);

        if (lineIndex) {
          itemNode.dataset.itemIndex = lineIndex;
          const removeLink = itemNode.querySelector('[data-item-remove]');

          if (removeLink) {
            removeLink.href = `${this.getRoute('cart_change_url', '/cart/change')}?line=${lineIndex}&quantity=0`;
          }
        }
      });
    }

    async updateDrawerLineItem(detail, item) {
      const quantity = Number(detail?.quantity);
      const itemKey = item?.dataset?.item || detail?.id || '';

      if (!item || !itemKey || Number.isNaN(quantity)) {
        return;
      }

      item.classList.toggle('is-loading', quantity > 0);
      item.classList.toggle('is-removed', quantity <= 0);

      try {
        const response = await fetch(this.getCartChangeJsonUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ id: itemKey, quantity }),
        });

        if (!response.ok) {
          throw new Error('Cart line update failed');
        }

        const body = await response.json();

        if (body?.errors) {
          throw new Error('Cart line update failed');
        }

        if (window.cart && typeof window.cart.getCart === 'function') {
          window.cart.getCart();
        }
      } catch (error) {
        item.classList.remove('is-loading', 'is-removed');
        throw error;
      }
    }

    renderGiftPromo(cartState) {
      const promo = this.getGiftPromo();
      if (!promo) return;

      const unlockQuantity = Number(promo.dataset.cartGiftUnlockQuantity || 2);
      const cycleSize = unlockQuantity + 1;
      const eligibleQuantity = Number(cartState?.eligibleQuantityCount || 0);
      const cyclePosition = eligibleQuantity % cycleSize;
      const completedFreePaintings = Math.floor(eligibleQuantity / cycleSize);
      const hasEarnedFree = completedFreePaintings > 0;
      const isUnlocked = eligibleQuantity > 0 && cyclePosition === unlockQuantity;
      const remainingQuantity = isUnlocked
        ? 0
        : cyclePosition === 0 && eligibleQuantity > 0
          ? unlockQuantity
          : Math.max(unlockQuantity - cyclePosition, 0);
      const message = promo.querySelector('[data-cart-gift-message]');
      const progress = promo.querySelector('[data-cart-gift-progress]');

      promo.dataset.cartGiftEligibleQuantity = String(eligibleQuantity);
      promo.classList.toggle('is-success', isUnlocked);
      promo.classList.toggle('has-step-one', eligibleQuantity >= 1);
      promo.classList.toggle('has-earned-free', hasEarnedFree);
      promo.toggleAttribute('hidden', !cartState?.hasVisibleItems);

      if (message) {
        if (isUnlocked && completedFreePaintings >= 1) {
          message.textContent = '🎉 Gelukt! Je krijgt nog 1 schilderij gratis. Kies je gratis schilderij.';
        } else if (isUnlocked) {
          message.textContent = '🎉 Gelukt! De derde krijg je van ons. Kies je gratis schilderij.';
        } else if (hasEarnedFree && completedFreePaintings === 1 && remainingQuantity === 2) {
          message.textContent = '🎉 Je krijgt 1 schilderij gratis. Voeg er nog 2 toe voor een tweede.';
        } else if (hasEarnedFree && completedFreePaintings === 1 && remainingQuantity === 1) {
          message.textContent = '🎉 Je krijgt 1 schilderij gratis. Voeg nog 1 schilderij toe voor een tweede.';
        } else if (hasEarnedFree) {
          message.textContent = `🎉 Je krijgt ${completedFreePaintings} schilderijen gratis. Voeg er nog ${remainingQuantity} toe voor nog één gratis schilderij.`;
        } else if (remainingQuantity === 1) {
          message.textContent = '🔥 Nog 1 schilderij — de derde krijg je van ons.';
        } else {
          message.textContent = '🔥 Nog 2 schilderijen — de derde krijg je van ons.';
        }
      }

      if (progress) {
        const value = Math.max(0, Math.min(100, (cyclePosition / unlockQuantity) * 100));
        progress.value = value;
      }
    }

    getStoredPreference() {
      try {
        return window.sessionStorage.getItem(PREFERENCE_KEY) !== 'false';
      } catch (error) {
        return this.memoryPreference;
      }
    }

    setStoredPreference(enabled) {
      this.memoryPreference = Boolean(enabled);

      try {
        if (enabled) {
          window.sessionStorage.removeItem(PREFERENCE_KEY);
        } else {
          window.sessionStorage.setItem(PREFERENCE_KEY, 'false');
        }
      } catch (error) {
        // Ignore storage errors in private browsing modes
      }
    }

    getCartJsonUrl() {
      return `${this.getRoute('cart_url', '/cart').replace(/\/$/, '')}.js`;
    }

    getSectionUrl() {
      return `${this.getRoute('cart_url', '/cart')}?section_id=${API_SECTION_ID}`;
    }

    hasRecentSyncFailure(windowMs = 3000) {
      return Boolean(this.failedSyncAt && Date.now() - this.failedSyncAt < windowMs);
    }

    noteSyncFailure() {
      this.failedSyncAt = Date.now();
    }

    async fetchCart() {
      const response = await fetch(this.getCartJsonUrl(), {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        this.noteSyncFailure();
        throw new Error(`Cart fetch failed: ${response.status}`);
      }

      try {
        const cart = await response.json();
        this.failedSyncAt = 0;
        return cart;
      } catch (error) {
        this.noteSyncFailure();
        throw new Error('Cart fetch returned invalid JSON');
      }
    }

    async fetchSummaryMarkup() {
      return this.fetchSectionFragment('[data-api-cart-summary]');
    }

    async fetchPriceMarkup() {
      return this.fetchSectionFragment('[data-api-cart-price]');
    }

    async fetchSectionFragment(selector) {
      const response = await fetch(this.getSectionUrl(), {
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) {
        this.noteSyncFailure();
        throw new Error(`Cart summary fetch failed: ${response.status}`);
      }

      const html = await response.text();
      const doc = new window.DOMParser().parseFromString(html, 'text/html');
      return doc.querySelector(selector);
    }

    deriveCartState(cart, options = {}) {
      let insuranceQuantity = 0;
      let visibleQuantityCount = 0;
      let eligibleQuantityCount = 0;
      let hasVisibleItems = false;
      const respectStoredPreference = options.respectStoredPreference !== false;

      (cart.items || []).forEach((item) => {
        const itemVariantId = String(item.variant_id || item.id || '');
        const quantity = Number(item.quantity || 0);

        if (itemVariantId === this.state.variantId) {
          insuranceQuantity += quantity;
          return;
        }

        hasVisibleItems = true;
        visibleQuantityCount += quantity;

        if (this.isGiftEligibleItem(item)) {
          eligibleQuantityCount += quantity;
        }
      });

      if (!hasVisibleItems) {
        this.setStoredPreference(true);
      }

      const desiredEnabled = hasVisibleItems
        ? (respectStoredPreference ? this.getStoredPreference() : insuranceQuantity > 0)
        : false;
      const desiredQuantity = hasVisibleItems && desiredEnabled ? 1 : 0;

      return {
        cart,
        hasVisibleItems,
        visibleQuantityCount,
        eligibleQuantityCount,
        insuranceQuantity,
        inCart: insuranceQuantity > 0,
        desiredEnabled,
        desiredQuantity,
        totalPrice: Number(cart.total_price || 0),
      };
    }

    getStateSignature(cartState) {
      return JSON.stringify({
        totalPrice: cartState.totalPrice,
        insuranceQuantity: cartState.insuranceQuantity,
        visibleQuantityCount: cartState.visibleQuantityCount,
        eligibleQuantityCount: cartState.eligibleQuantityCount,
        hasVisibleItems: cartState.hasVisibleItems,
        items: (cartState.cart.items || []).map((item) => `${item.key || item.variant_id || item.id}:${item.quantity}`),
      });
    }

    hasStateDrift(referenceState, liveState) {
      return this.getStateSignature(referenceState) !== this.getStateSignature(liveState);
    }

    setLoading(isLoading) {
      this.state.loading = isLoading;
      this.syncLiveState();
    }

    setError(message) {
      this.state.errorMessage = message || '';

      const errorNode = this.getErrorNode();
      if (!errorNode) return;

      if (message) {
        errorNode.textContent = message;
        errorNode.classList.remove('is-hidden');
      } else {
        errorNode.textContent = '';
        errorNode.classList.add('is-hidden');
      }
    }

    syncLiveState() {
      const block = this.getProtectionBlock();
      const toggle = this.getToggle();

      if (toggle) {
        toggle.disabled = this.state.loading;
      }

      if (block) {
        block.classList.toggle('is-loading', this.state.loading);
      }

      this.setError(this.state.errorMessage);
    }

    prehydrateStoredPreference() {
      const block = this.getProtectionBlock();
      const toggle = this.getToggle();

      if (!block || !toggle) return;

      const hasVisibleItems = block.dataset.owaShippingProtectionHasVisibleItems === 'true';
      const inCart = block.dataset.owaShippingProtectionInCart === 'true';
      const desiredChecked = hasVisibleItems ? (inCart || this.getStoredPreference()) : false;

      toggle.checked = desiredChecked;
      block.toggleAttribute('hidden', !hasVisibleItems);
    }

    applyStateToDom(cartState) {
      const block = this.getProtectionBlock();
      const toggle = this.getToggle();

      this.renderGiftPromo(cartState);
      this.syncDrawerLineIndexes(cartState?.cart);

      if (block) {
        block.dataset.owaShippingProtectionVariantId = this.state.variantId;
        block.dataset.owaShippingProtectionInCart = cartState.inCart ? 'true' : 'false';
        block.dataset.owaShippingProtectionHasVisibleItems = cartState.hasVisibleItems ? 'true' : 'false';
        block.toggleAttribute('hidden', !cartState.hasVisibleItems);
        block.classList.toggle('is-loading', this.state.loading);
      }

      if (toggle) {
        toggle.checked = cartState.desiredEnabled;
        toggle.disabled = this.state.loading;
      }

      this.setError(this.state.errorMessage);
    }

    syncThemeCartReferences() {
      if (!window.cart) return;

      const drawer = this.getDrawer();
      window.cart.cartDrawer = drawer || window.cart.cartDrawer || null;
      window.cart.cart = drawer || window.cart.cart || null;
      window.cart.cartForm = drawer?.querySelector('[data-cart-form]') || window.cart.cartForm || null;
      window.cart.cartEmpty = drawer?.querySelector('[data-cart-empty]') || window.cart.cartEmpty || null;
      window.cart.buttonHolder = drawer?.querySelector('[data-foot-holder]') || window.cart.buttonHolder || null;
      window.cart.itemsHolder = drawer?.querySelector('[data-items-holder]') || window.cart.itemsHolder || null;
      window.cart.upsellProductsHolder = drawer?.querySelector('[data-upsell-products]') || window.cart.upsellProductsHolder || null;
      window.cart.priceHolder = drawer?.querySelector('[data-cart-price-holder]') || window.cart.priceHolder || null;
      window.cart.cartTotal = drawer?.querySelector('[data-cart-total]') || window.cart.cartTotal || null;
    }

    wrapThemeCartBuild() {
      if (!window.cart || this.isThemeCartBuildWrapped || typeof window.cart.build !== 'function') {
        return;
      }

      const originalBuild = window.cart.build.bind(window.cart);

      window.cart.build = (...args) => {
        this.syncThemeCartReferences();

        try {
          return originalBuild(...args);
        } finally {
          this.syncThemeCartReferences();
          this.syncLiveState();
        }
      };

      this.isThemeCartBuildWrapped = true;
    }

    syncThemeTotals(totalPrice) {
      if (!window.cart) return;

      window.cart.subtotal = Number(totalPrice || 0);
      if (window.theme) {
        window.theme.subtotal = window.cart.subtotal;
      }

      if (typeof window.cart.freeShippingMessageHandle === 'function') {
        window.cart.freeShippingMessageHandle(window.cart.subtotal);
      }

      if (typeof window.cart.updateProgress === 'function') {
        window.cart.updateProgress();
      }
    }

    rollbackInterruptedPreference(job) {
      if (job?.reason !== 'toggle-change') return;
      if (this.pendingJob?.reason === 'toggle-change') return;
    }

    async refreshSummary(renderState = null) {
      const apiSummary = await this.fetchSummaryMarkup();
      if (!apiSummary) {
        throw new Error('Cart summary render failed');
      }

      if (this.pendingJob) {
        return false;
      }

      const summaryHolder = this.getSummaryHolder();
      if (!summaryHolder) {
        throw new Error('Cart summary holder missing');
      }

      summaryHolder.innerHTML = apiSummary.innerHTML;
      this.syncThemeCartReferences();
      if (renderState) {
        this.applyStateToDom(renderState);
      } else {
        this.syncLiveState();
      }
      return true;
    }

    async refreshPriceHolder() {
      const apiPrice = await this.fetchPriceMarkup();
      if (!apiPrice) {
        throw new Error('Cart price render failed');
      }

      const priceHolder = this.getDrawer()?.querySelector('[data-cart-price-holder]');
      if (!priceHolder) {
        throw new Error('Cart price holder missing');
      }

      priceHolder.innerHTML = apiPrice.innerHTML;
      this.syncThemeCartReferences();
      return true;
    }

    async mutateInsurance(quantity) {
      const response = await fetch(this.getRoute('cart_update_url', '/cart/update.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          updates: { [this.state.variantId]: quantity },
        }),
      });

      if (!response.ok) {
        throw new Error('Shipping protection update failed');
      }

      return response.json();
    }

    scheduleReconcile(job = {}) {
      if (this.hasRecentSyncFailure() && !job.showErrors && job.reason !== 'toggle-change') {
        return;
      }

      const respectStoredPreference = job.respectStoredPreference !== false;
      const modePriority = Number(job.modePriority || 0);
      const allowCoreRefreshFallback = job.allowCoreRefreshFallback !== false;
      const pendingModePriority = this.pendingJob ? Number(this.pendingJob.modePriority || 0) : -1;
      const keepPendingMode = Boolean(this.pendingJob && (job.preservePendingMode || pendingModePriority > modePriority));

      this.pendingJob = {
        reason: job.reason || this.pendingJob?.reason || 'unknown',
        refreshSummary: Boolean(this.pendingJob?.refreshSummary || job.refreshSummary),
        showErrors: Boolean(this.pendingJob?.showErrors || job.showErrors),
        respectStoredPreference: keepPendingMode
          ? this.pendingJob.respectStoredPreference !== false
          : respectStoredPreference,
        modePriority: keepPendingMode ? pendingModePriority : modePriority,
        allowCoreRefreshFallback: keepPendingMode
          ? this.pendingJob.allowCoreRefreshFallback !== false
          : allowCoreRefreshFallback,
      };

      if (this.isProcessing) return;

      window.queueMicrotask(() => this.processQueue());
    }

    scheduleResync(job = {}) {
      this.scheduleReconcile({
        reason: job.reason || this.pendingJob?.reason || 'summary-resync',
        refreshSummary: true,
        showErrors: Boolean(this.pendingJob?.showErrors || job.showErrors),
        preservePendingMode: true,
        modePriority: job.modePriority,
        allowCoreRefreshFallback: job.allowCoreRefreshFallback,
        respectStoredPreference: job.respectStoredPreference,
      });
    }

    triggerCoreCartRefresh(job = {}) {
      if (!window.cart || typeof window.cart.getCart !== 'function') {
        return false;
      }

      if (this.fallbackSignalResetTimer) {
        window.clearTimeout(this.fallbackSignalResetTimer);
        this.fallbackSignalResetTimer = 0;
      }
      this.awaitingFallbackRefresh = true;
      this.ignoringFallbackSignals = true;
      let settled = false;
      let timer = 0;

      const finish = () => {
        if (settled) return;
        settled = true;
        this.awaitingFallbackRefresh = false;
        if (timer) {
          window.clearTimeout(timer);
        }
        document.removeEventListener('theme:cart:change', onCartBuilt);
        document.removeEventListener('theme:product:added', onCartBuilt);
        this.setLoading(false);
        this.syncThemeCartReferences();
        this.syncLiveState();
        this.scheduleResync({
          reason: job.reason || 'fallback-rebuild-resync',
          showErrors: job.showErrors,
          modePriority: job.modePriority,
          allowCoreRefreshFallback: false,
          respectStoredPreference: job.respectStoredPreference,
        });
        this.fallbackSignalResetTimer = window.setTimeout(() => {
          this.ignoringFallbackSignals = false;
          this.fallbackSignalResetTimer = 0;
        }, 0);
      };

      const onCartBuilt = () => finish();

      document.addEventListener('theme:cart:change', onCartBuilt, { once: true });
      document.addEventListener('theme:product:added', onCartBuilt, { once: true });

      timer = window.setTimeout(() => finish(), 1500);
      try {
        window.cart.getCart();
      } catch (error) {
        finish();
      }
      return true;
    }

    async processQueue() {
      if (this.isProcessing) return;

      this.isProcessing = true;

      while (this.pendingJob) {
        const job = this.pendingJob;
        this.pendingJob = null;
        await this.reconcileFromCart(job);
      }

      this.isProcessing = false;
    }

    async recoverAfterFailure(job) {
      let recoveryState = null;

      try {
        const cart = await this.fetchCart();
        let cartState = this.deriveCartState(cart, { respectStoredPreference: false });
        recoveryState = cartState;

        if (this.pendingJob) {
          this.rollbackInterruptedPreference(job);
          return;
        }

        const confirmedPreference = cartState.hasVisibleItems ? cartState.inCart : true;

        this.state.lastConfirmedPreference = confirmedPreference;
        cartState = this.deriveCartState(cart, {
          respectStoredPreference: job.respectStoredPreference,
        });
        recoveryState = cartState;
        let usedCoreRefreshFallback = false;

        try {
          const didRefresh = await this.refreshSummary(cartState);
          if (!didRefresh) {
            return;
          }
        } catch (summaryError) {
          let refreshedPriceHolder = false;

          try {
            refreshedPriceHolder = await this.refreshPriceHolder();
          } catch (priceError) {
            refreshedPriceHolder = false;
          }

          if (!refreshedPriceHolder && job.allowCoreRefreshFallback !== false && !this.hasRecentSyncFailure()) {
            if (this.triggerCoreCartRefresh({
              reason: 'fallback-rebuild-resync',
              showErrors: job.showErrors,
              modePriority: job.modePriority,
              allowCoreRefreshFallback: false,
              respectStoredPreference: job.respectStoredPreference,
            })) {
              usedCoreRefreshFallback = true;
            }
          }
        }

        if (usedCoreRefreshFallback) {
          this.setError(job.showErrors ? ERROR_MESSAGE : '');
          this.syncLiveState();
          return;
        }

        const liveCart = await this.fetchCart();
        const liveState = this.deriveCartState(liveCart, {
          respectStoredPreference: job.respectStoredPreference,
        });

        if (this.pendingJob || this.hasStateDrift(cartState, liveState)) {
          this.scheduleResync({
            reason: 'recovery-resync',
            showErrors: job.showErrors,
            modePriority: job.modePriority,
            allowCoreRefreshFallback: job.allowCoreRefreshFallback,
            respectStoredPreference: job.respectStoredPreference,
          });
          return;
        }

        this.state.lastConfirmedPreference = liveState.hasVisibleItems ? liveState.desiredEnabled : true;
        this.setError(job.showErrors ? ERROR_MESSAGE : '');
        this.syncThemeCartReferences();
        this.syncThemeTotals(liveState.totalPrice);
        this.applyStateToDom(liveState);
      } catch (syncError) {
        if (this.pendingJob) {
          this.rollbackInterruptedPreference(job);
          return;
        }
        const rollbackPreference = recoveryState
          ? (recoveryState.hasVisibleItems ? recoveryState.inCart : true)
          : this.state.lastConfirmedPreference;
        this.state.lastConfirmedPreference = rollbackPreference;
        try {
          await this.refreshPriceHolder();
        } catch (priceError) {
          // Leave the last server-rendered summary in place if the fragment refresh is unavailable.
        }
        this.setError(job.showErrors ? ERROR_MESSAGE : '');
        this.syncThemeCartReferences();
        if (recoveryState) {
          this.syncThemeTotals(recoveryState.totalPrice);
        }
        this.syncLiveState();

        if (recoveryState) {
          this.applyStateToDom({
            ...recoveryState,
            desiredEnabled: job.reason === 'toggle-change'
              ? rollbackPreference
              : recoveryState.desiredEnabled,
          });
        }

        const toggle = this.getToggle();
        if (toggle && !recoveryState) {
          toggle.checked = rollbackPreference;
        }
      } finally {
        if (!this.awaitingFallbackRefresh) {
          this.setLoading(false);
        }
      }
    }

    async reconcileFromCart(job = {}) {
      try {
        const cart = await this.fetchCart();
        const cartState = this.deriveCartState(cart, {
          respectStoredPreference: job.respectStoredPreference,
        });

        this.state.errorMessage = '';
        this.applyStateToDom(cartState);

        if (cartState.insuranceQuantity === cartState.desiredQuantity) {
          if (job.refreshSummary) {
            const didRefresh = await this.refreshSummary(cartState);
            if (!didRefresh) {
              return;
            }
            const liveCart = await this.fetchCart();
            const liveState = this.deriveCartState(liveCart, {
              respectStoredPreference: job.respectStoredPreference,
            });

            if (this.pendingJob || this.hasStateDrift(cartState, liveState)) {
              this.scheduleResync({
                reason: 'no-op-resync',
                showErrors: job.showErrors,
                modePriority: job.modePriority,
                allowCoreRefreshFallback: job.allowCoreRefreshFallback,
                respectStoredPreference: job.respectStoredPreference,
              });
              return;
            }

            this.syncThemeCartReferences();
            this.syncThemeTotals(liveState.totalPrice);
            if (!this.pendingJob) {
              this.state.lastConfirmedPreference = liveState.hasVisibleItems ? liveState.desiredEnabled : true;
              this.applyStateToDom(liveState);
            }
            return;
          }
          this.syncThemeCartReferences();
          if (!this.pendingJob) {
            this.state.lastConfirmedPreference = cartState.hasVisibleItems ? cartState.desiredEnabled : true;
            this.syncThemeTotals(cartState.totalPrice);
            this.applyStateToDom(cartState);
          }
          return;
        }

        this.setLoading(true);
        this.applyStateToDom(cartState);

        const updatedCart = await this.mutateInsurance(cartState.desiredQuantity);
        const confirmedState = this.deriveCartState(updatedCart, { respectStoredPreference: false });

        const didRefresh = await this.refreshSummary(confirmedState);
        if (!didRefresh) {
          this.setLoading(false);
          return;
        }
        const liveCart = await this.fetchCart();
        const liveState = this.deriveCartState(liveCart, {
          respectStoredPreference: job.respectStoredPreference,
        });

        if (this.pendingJob || this.hasStateDrift(confirmedState, liveState)) {
          this.state.errorMessage = '';
          this.setLoading(false);
          this.scheduleResync({
            reason: 'mutation-resync',
            showErrors: job.showErrors,
            modePriority: job.modePriority,
            allowCoreRefreshFallback: job.allowCoreRefreshFallback,
            respectStoredPreference: job.respectStoredPreference,
          });
          return;
        }

        this.state.errorMessage = '';
        this.setLoading(false);
        if (!this.pendingJob) {
          this.state.lastConfirmedPreference = liveState.hasVisibleItems ? liveState.desiredEnabled : true;
          this.syncThemeTotals(liveState.totalPrice);
          this.applyStateToDom(liveState);
        }
      } catch (error) {
        await this.recoverAfterFailure(job);
      }
    }

    handleToggleChange(event) {
      const toggle = event.target.closest('[data-owa-shipping-protection-toggle]');
      const drawer = this.getDrawer();
      if (!toggle || !drawer || !drawer.contains(toggle)) return;

      this.setStoredPreference(Boolean(toggle.checked));
      this.scheduleReconcile({
        reason: 'toggle-change',
        modePriority: 2,
        showErrors: true,
      });
    }

    handleCartSignal(event) {
      const isFallbackSignal = event?.type === 'theme:cart:change' || event?.type === 'theme:product:added';

      if (this.ignoringFallbackSignals && isFallbackSignal) {
        return;
      }

      if (this.cartSignalTimer) {
        window.clearTimeout(this.cartSignalTimer);
      }

      const eventType = event?.type || 'cart-signal';
      const refreshSummary = eventType !== 'theme:cart:open';

      this.cartSignalTimer = window.setTimeout(() => {
        this.cartSignalTimer = 0;
        this.scheduleReconcile({
          reason: eventType,
          refreshSummary,
          modePriority: 1,
          respectStoredPreference: true,
        });
      }, 120);
    }

    handleDrawerLineItemFailure() {
      this.state.errorMessage = ERROR_MESSAGE;
      this.syncLiveState();

      if (window.cart && typeof window.cart.getCart === 'function' && !this.hasRecentSyncFailure()) {
        this.syncThemeCartReferences();

        try {
          window.cart.getCart();
        } catch (error) {
          // Fall through to the summary reconcile below.
        }
      }

      this.scheduleReconcile({
        reason: 'drawer-line-item-failure',
        refreshSummary: true,
        showErrors: true,
        modePriority: 2,
        respectStoredPreference: true,
      });
    }

    handleDrawerRemoveClick(event) {
      const trigger = event.target.closest('[data-item-remove]');
      const item = this.getDrawerItem(trigger);

      if (!trigger || !item) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      if (this.isDisabledControl(trigger)) {
        return;
      }

      this.updateDrawerLineItem(
        {
          id: trigger.dataset.id || item.dataset.item || '',
          quantity: 0,
        },
        item
      ).catch(() => {
        this.handleDrawerLineItemFailure();
      });
    }

    handleDrawerQuantityUpdate(event) {
      const item = this.getDrawerItem(event.target);

      if (!item) {
        return;
      }

      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      this.updateDrawerLineItem(event.detail || {}, item).catch(() => {
        this.handleDrawerLineItemFailure();
      });
    }
  }

  const controller = new CartDrawerShippingProtection();
  controller.init();
})();
